import { useEffect } from "react";

const SITE = "BeforeExam";
const DEFAULT_DESC =
  "Pass your next certification with real-exam practice tests, adaptive analytics and a streak-powered prep engine for AWS, Azure, GCP, Kubernetes, DevOps and more.";

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-jsonld", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

/**
 * useSEO — declarative SEO for any page.
 * @param {{title?:string, description?:string, image?:string, jsonLd?:object|object[], canonical?:string, noindex?:boolean}} cfg
 */
export function useSEO(cfg = {}) {
  useEffect(() => {
    const title = cfg.title ? `${cfg.title} · ${SITE}` : `${SITE} — Pass your next certification`;
    const desc = cfg.description || DEFAULT_DESC;
    const url = cfg.canonical || (typeof window !== "undefined" ? window.location.href : "");

    document.title = title;
    setMeta("description", desc);
    setMeta("og:title", title, "property");
    setMeta("og:description", desc, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", url, "property");
    if (cfg.image) setMeta("og:image", cfg.image, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", desc);
    if (cfg.image) setMeta("twitter:image", cfg.image);
    setMeta("robots", cfg.noindex ? "noindex,nofollow" : "index,follow");

    if (url) setLink("canonical", url);

    if (cfg.jsonLd) {
      const items = Array.isArray(cfg.jsonLd) ? cfg.jsonLd : [cfg.jsonLd];
      items.forEach((d, i) => upsertJsonLd(`page-${i}`, d));
    } else {
      // remove old json-lds
      document.head.querySelectorAll('script[data-jsonld^="page-"]').forEach((n) => n.remove());
    }

    return () => {
      // Don't clear title — let next page set it.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg.title, cfg.description, cfg.image, cfg.canonical, cfg.noindex, JSON.stringify(cfg.jsonLd || null)]);
}

export const SITE_NAME = SITE;
